import { useState, useEffect } from "react";
import { ethers } from "ethers";
import { toast } from "react-toastify";
import { getContract } from "../config/contract";
import { CATEGORIES, CURRENCY, inrToEth } from "../config/config";
import { notifyTransactionSubmitted, notifyTransactionConfirmed, notifyTransactionFailed } from "../utils/notifications";
import { getCampaignImage } from "../utils/categoryImages";
import { addActivity } from "../services/firebaseService";
import { validateCampaignForm } from "../utils/validation";
import geminiService from "../services/geminiService";

const CreateCampaign = ({ onSuccess, onClose, standalone = false }) => {
  // Add CSS animation for spinner
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    goalAmount: "",
    duration: "",
    imageURI: "",
    category: CATEGORIES[0],
    creatorInfo: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // AI Improvement States
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);

  // Auto-suggest category when title/description changes
  useEffect(() => {
    const suggestCategory = async () => {
      if (formData.title && formData.description && geminiService.isAvailable()) {
        try {
          const result = await geminiService.suggestCategory(formData.title, formData.description);
          if (result.success && result.suggestedCategory !== formData.category) {
            // Automatically update the category
            setFormData(prev => ({
              ...prev,
              category: result.suggestedCategory
            }));
            
            toast.success(`AI updated category to "${result.suggestedCategory}"`);
          }
        } catch (error) {
          console.error('Category suggestion failed:', error);
        }
      }
    };

    const timeoutId = setTimeout(suggestCategory, 2000); // Debounce
    return () => clearTimeout(timeoutId);
  }, [formData.title, formData.description, formData.category]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // AI Improvement Functions
  const getAIImprovement = async () => {
    if (!formData.title || !formData.description) {
      toast.error("Please enter title and description first");
      return;
    }

    if (!geminiService.isAvailable()) {
      const initError = geminiService.getInitError();
      let errorMessage = "AI improvement not available";
      
      if (initError?.message?.includes('API key')) {
        errorMessage = "AI service requires valid API key configuration";
      } else if (initError?.message?.includes('quota')) {
        errorMessage = "AI service quota exceeded - please try again later";
      } else if (initError?.message?.includes('billing')) {
        errorMessage = "AI service requires billing to be enabled";
      }
      
      toast.info(errorMessage);
      return;
    }

    setLoadingAI(true);
    try {
      const result = await geminiService.improveDescription(
        formData.title,
        formData.description,
        formData.category
      );

      if (result.success) {
        setAiSuggestions(result);
        setQualityScore(result.qualityScore);
        setShowAISuggestions(true);
        toast.success("AI analysis complete!");
      } else {
        toast.error(result.message || "AI improvement failed");
      }
    } catch (error) {
      console.error('AI improvement error:', error);
      toast.error("AI service temporarily unavailable");
    } finally {
      setLoadingAI(false);
    }
  };

  const applyAIImprovement = () => {
    if (aiSuggestions?.improvedDescription) {
      setFormData({
        ...formData,
        description: aiSuggestions.improvedDescription
      });
      setShowAISuggestions(false);
      toast.success("AI improvement applied!");
    }
  };
  // Upload image to IPFS
  const uploadImageToIPFS = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const pinataMetadata = JSON.stringify({
      name: `Campaign-Image-${Date.now()}`,
      keyvalues: {
        type: 'campaign-banner'
      }
    });
    formData.append('pinataMetadata', pinataMetadata);

    const pinataOptions = JSON.stringify({
      cidVersion: 0,
    });
    formData.append('pinataOptions', pinataOptions);

    try {
      const response = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_PINATA_JWT}`,
        },
        body: formData,
      });

      const result = await response.json();
      return `https://gateway.pinata.cloud/ipfs/${result.IpfsHash}`;
    } catch (error) {
      console.error('IPFS upload error:', error);
      throw error;
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload only images (JPG, PNG)');
        return;
      }

      if (file.size > maxSize) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setSelectedImage(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return;

    setUploadingImage(true);
    try {
      toast.info('Uploading image to IPFS...');
      const ipfsUrl = await uploadImageToIPFS(selectedImage);
      
      setFormData({
        ...formData,
        imageURI: ipfsUrl
      });
      
      toast.success('Image uploaded successfully!');
      setSelectedImage(null);
      document.getElementById('campaign-image-input').value = '';
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Image upload failed. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    const validation = validateCampaignForm(formData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }
    
    setLoading(true);

    try {
      const { contract } = await getContract();
      const sanitizedData = validation.sanitizedData;

      const goalInEth = inrToEth(sanitizedData.goalAmount);
      const goalInWei = ethers.parseEther(goalInEth);
      const durationInSeconds = sanitizedData.duration * 24 * 60 * 60;

      toast.info("Please confirm campaign creation in MetaMask...");
      const tx = await contract.createCampaign(
        sanitizedData.title,
        sanitizedData.description,
        goalInWei,
        durationInSeconds,
        getCampaignImage(sanitizedData.imageURI, sanitizedData.category),
        sanitizedData.category,
        sanitizedData.creatorInfo
      );

      await notifyTransactionSubmitted(tx.hash);
      
      const receipt = await tx.wait();
      
      if (receipt.status === 1) {
        // Add activity to Firebase
        await addActivity('campaign_created', {
          campaignTitle: sanitizedData.title,
          category: sanitizedData.category,
          goalAmount: goalInEth,
          creator: sanitizedData.creatorInfo || 'Anonymous'
        });

        const { invalidateCache } = await import("../utils/dataCache");
        invalidateCache();
        
        await notifyTransactionConfirmed('campaign', {
          body: `Campaign "${sanitizedData.title}" has been created successfully!`
        });
        onSuccess();
      } else {
        await notifyTransactionFailed("Transaction failed", "campaign");
      }
    } catch (error) {
      console.error(error);
      let errorMessage = "Campaign creation failed";
      
      if (error.code === 'ACTION_REJECTED') {
        errorMessage = "Transaction was rejected by user";
      } else if (error.message?.includes('insufficient funds')) {
        errorMessage = "Insufficient funds for transaction";
      } else if (error.message?.includes('gas')) {
        errorMessage = "Transaction failed due to gas issues";
      } else if (error.reason) {
        errorMessage = `Transaction failed: ${error.reason}`;
      }
      
      await notifyTransactionFailed(errorMessage, "campaign");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={standalone ? styles.standalonePage : styles.overlay}>
      <div style={standalone ? styles.standaloneModal : styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            Create New Campaign
          </h2>
          {!standalone && (
            <button style={styles.closeBtn} onClick={onClose}>
              X
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Campaign Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              style={styles.input}
              placeholder="Enter campaign title"
            />
          </div>

          <div style={styles.formGroup}>
            <div style={styles.labelRow}>
              <label style={styles.label}>Description *</label>
              {geminiService.isAvailable() && (
                <button
                  type="button"
                  onClick={getAIImprovement}
                  disabled={loadingAI || !formData.title || !formData.description}
                  style={{
                    ...styles.aiBtn,
                    opacity: loadingAI ? 0.7 : 1,
                    cursor: loadingAI ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loadingAI ? (
                    <>
                      <span style={styles.spinner}></span>
                      🤖 Analyzing...
                    </>
                  ) : (
                    "✨ AI Improve"
                  )}
                </button>
              )}
            </div>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              style={{ ...styles.input, minHeight: "100px" }}
              placeholder="Describe your campaign"
            />
            
            {/* Quality Score Display */}
            {qualityScore && (
              <div style={styles.qualityScore}>
                <span style={styles.scoreLabel}>AI Quality Score: </span>
                <span style={{
                  ...styles.scoreValue,
                  color: qualityScore >= 8 ? "#10b981" : qualityScore >= 6 ? "#f59e0b" : "#ef4444"
                }}>
                  {qualityScore}/10
                </span>
              </div>
            )}
          </div>

          {/* AI Suggestions Panel */}
          {showAISuggestions && aiSuggestions && (
            <div style={styles.aiSuggestions}>
              <div style={styles.aiHeader}>
                <h4 style={styles.aiTitle}>🤖 AI Improvement Suggestions</h4>
                <button
                  type="button"
                  onClick={() => setShowAISuggestions(false)}
                  style={styles.closeAiBtn}
                >
                  ×
                </button>
              </div>
              
              <div style={styles.aiContent}>
                <div style={styles.scoreSection}>
                  <span style={styles.scoreLabel}>Quality Score: </span>
                  <span style={styles.scoreValue}>{aiSuggestions.qualityScore}/10</span>
                  <p style={styles.scoreExplanation}>{aiSuggestions.scoreExplanation}</p>
                </div>
                
                {aiSuggestions.suggestions && aiSuggestions.suggestions.length > 0 && (
                  <div style={styles.suggestionsSection}>
                    <h5 style={styles.suggestionsTitle}>Improvement Suggestions:</h5>
                    <ul style={styles.suggestionsList}>
                      {aiSuggestions.suggestions.map((suggestion, index) => (
                        <li key={index} style={styles.suggestionItem}>{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {aiSuggestions.improvedDescription && (
                  <div style={styles.improvedSection}>
                    <h5 style={styles.improvedTitle}>Improved Description:</h5>
                    <div style={styles.improvedText}>{aiSuggestions.improvedDescription}</div>
                    <button
                      type="button"
                      onClick={applyAIImprovement}
                      style={styles.applyBtn}
                    >
                      Apply Improvement
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Goal Amount ({CURRENCY.symbol} INR) *</label>
              <input
                type="number"
                name="goalAmount"
                value={formData.goalAmount}
                onChange={handleChange}
                required
                step="100"
                min="0"
                style={styles.input}
                placeholder="10000"
              />
              <div style={styles.ethEquivalent}>
                ≈ {formData.goalAmount ? inrToEth(formData.goalAmount) : '0'} ETH
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Duration (Days) *</label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                required
                min="1"
                style={styles.input}
                placeholder="30"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Category *</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              style={styles.input}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Image Upload Section - Same as original */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Campaign Banner Image</label>
            <div style={styles.optionalNote}>
              Upload a custom image or use the default category image
            </div>
            
            <div style={formData.imageURI ? styles.imageUploadSectionSuccess : styles.imageUploadSection}>
              <div style={styles.uploadOptions}>
                <div style={styles.uploadOption}>
                  <input
                    id="campaign-image-input"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleImageSelect}
                    style={styles.hiddenInput}
                  />
                  <label htmlFor="campaign-image-input" style={styles.uploadLabel}>
                    Choose Image (JPG, PNG) *
                  </label>
                  
                  {selectedImage && (
                    <div style={styles.selectedImageInfo}>
                      <span style={styles.imageName}>{selectedImage.name}</span>
                      <button
                        type="button"
                        onClick={handleImageUpload}
                        disabled={uploadingImage}
                        style={styles.uploadBtn}
                      >
                        {uploadingImage ? 'Uploading...' : 'Upload to IPFS'}
                      </button>
                    </div>
                  )}
                </div>
                
                <div style={styles.orDivider}>OR</div>
                
                <div style={styles.urlOption}>
                  <input
                    type="text"
                    name="imageURI"
                    value={formData.imageURI}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Paste image URL here"
                  />
                </div>
              </div>
              
              <div style={styles.imagePreview}>
                <div style={styles.imagePreviewHeader}>
                  <span style={styles.imagePreviewLabel}>
                    {formData.imageURI ? "Custom Campaign Image:" : `Default ${formData.category} Image:`}
                  </span>
                  <span style={formData.imageURI ? styles.imageSuccess : styles.imageDefault}>
                    {formData.imageURI ? "Custom Image" : "Category Default"}
                  </span>
                </div>
                <img 
                  src={getCampaignImage(formData.imageURI, formData.category)} 
                  alt="Campaign preview" 
                  style={styles.previewImage}
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/400x200?text=Campaign";
                  }}
                />
                {!formData.imageURI && (
                  <div style={styles.categoryImageNote}>
                    This default image will be used for your {formData.category} campaign. 
                    Upload a custom image above to replace it.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Creator Info</label>
            <input
              type="text"
              name="creatorInfo"
              value={formData.creatorInfo}
              onChange={handleChange}
              style={styles.input}
              placeholder="Your name or social link"
            />
          </div>

          <div style={styles.actions}>
            {!standalone && (
              <button
                type="button"
                onClick={onClose}
                style={styles.cancelBtn}
                disabled={loading}
              >
                Cancel
              </button>
            )}
            <button type="submit" style={styles.submitBtn} disabled={loading}>
              {loading ? "Creating..." : "Create Campaign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// Styles (keeping original styles and adding AI-specific ones)
const styles = {
  // ... (keeping all original styles from CreateCampaign.js)
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
    padding: "1rem",
  },
  standalonePage: {
    minHeight: "100vh",
    background: "#f9fafb",
    padding: "2rem 0",
  },
  modal: {
    background: "white",
    borderRadius: "20px",
    maxWidth: "700px",
    width: "100%",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
  },
  standaloneModal: {
    background: "white",
    borderRadius: "20px",
    maxWidth: "800px",
    width: "100%",
    margin: "0 auto",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1.5rem",
    borderBottom: "1px solid #eee",
  },
  title: {
    margin: 0,
    fontSize: "1.5rem",
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  aiLabel: {
    fontSize: "0.8rem",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: "0.25rem 0.5rem",
    borderRadius: "8px",
    fontWeight: "600",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    color: "#999",
  },
  form: {
    padding: "1.5rem",
  },
  formGroup: {
    marginBottom: "1.5rem",
    flex: 1,
  },
  labelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.5rem",
  },
  label: {
    display: "block",
    fontWeight: "600",
    color: "#333",
  },
  aiBtn: {
    padding: "0.5rem 1rem",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.85rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  spinner: {
    width: "12px",
    height: "12px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  input: {
    width: "100%",
    padding: "0.75rem",
    border: "2px solid #e0e0e0",
    borderRadius: "10px",
    fontSize: "1rem",
    boxSizing: "border-box",
    transition: "border-color 0.3s",
  },
  qualityScore: {
    marginTop: "0.5rem",
    padding: "0.5rem",
    background: "#f8fafc",
    borderRadius: "6px",
    fontSize: "0.9rem",
  },
  scoreLabel: {
    fontWeight: "600",
    color: "#374151",
  },
  scoreValue: {
    fontWeight: "700",
    fontSize: "1rem",
  },
  // AI Suggestions Styles
  aiSuggestions: {
    background: "#f0f9ff",
    border: "2px solid #0ea5e9",
    borderRadius: "12px",
    padding: "1rem",
    marginBottom: "1.5rem",
  },
  aiHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  aiTitle: {
    margin: 0,
    fontSize: "1.1rem",
    color: "#0c4a6e",
    fontWeight: "700",
  },
  closeAiBtn: {
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    color: "#6b7280",
  },
  aiContent: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  scoreSection: {
    padding: "0.75rem",
    background: "rgba(255, 255, 255, 0.7)",
    borderRadius: "8px",
  },
  scoreExplanation: {
    margin: "0.5rem 0 0 0",
    fontSize: "0.9rem",
    color: "#374151",
    fontStyle: "italic",
  },
  suggestionsSection: {
    padding: "0.75rem",
    background: "rgba(255, 255, 255, 0.7)",
    borderRadius: "8px",
  },
  suggestionsTitle: {
    margin: "0 0 0.5rem 0",
    fontSize: "1rem",
    color: "#0c4a6e",
    fontWeight: "600",
  },
  suggestionsList: {
    margin: 0,
    paddingLeft: "1.5rem",
  },
  suggestionItem: {
    marginBottom: "0.25rem",
    fontSize: "0.9rem",
    color: "#374151",
    lineHeight: "1.4",
  },
  improvedSection: {
    padding: "0.75rem",
    background: "rgba(255, 255, 255, 0.7)",
    borderRadius: "8px",
  },
  improvedTitle: {
    margin: "0 0 0.5rem 0",
    fontSize: "1rem",
    color: "#0c4a6e",
    fontWeight: "600",
  },
  improvedText: {
    padding: "0.75rem",
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    fontSize: "0.9rem",
    lineHeight: "1.5",
    color: "#374151",
    marginBottom: "0.75rem",
  },
  applyBtn: {
    padding: "0.5rem 1rem",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "0.9rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  ethEquivalent: {
    fontSize: "0.85rem",
    color: "#6b7280",
    marginTop: "0.25rem",
  },
  row: {
    display: "flex",
    gap: "1rem",
  },
  actions: {
    display: "flex",
    gap: "1rem",
    marginTop: "2rem",
  },
  cancelBtn: {
    flex: 1,
    padding: "0.75rem",
    border: "2px solid #ddd",
    background: "white",
    borderRadius: "10px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    color: "#666",
  },
  submitBtn: {
    flex: 1,
    padding: "0.75rem",
    border: "none",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    borderRadius: "10px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  // Image upload styles (keeping original)
  imageUploadSection: {
    border: "2px dashed #e0e0e0",
    borderRadius: "10px",
    padding: "1rem",
    background: "#fafafa",
  },
  imageUploadSectionSuccess: {
    border: "2px dashed #10b981",
    borderRadius: "10px",
    padding: "1rem",
    background: "#f0fdf4",
  },
  optionalNote: {
    fontSize: "0.85rem",
    color: "#6b7280",
    marginBottom: "0.5rem",
    fontWeight: "500",
    fontStyle: "italic",
  },
  uploadOptions: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  uploadOption: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  hiddenInput: {
    display: "none",
  },
  uploadLabel: {
    display: "inline-block",
    padding: "0.75rem 1.5rem",
    background: "#ffffff",
    border: "2px solid #e0e0e0",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "600",
    color: "#374151",
    textAlign: "center",
    transition: "all 0.2s ease",
  },
  selectedImageInfo: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    padding: "0.5rem",
    background: "#e0f2fe",
    borderRadius: "6px",
  },
  imageName: {
    fontSize: "0.9rem",
    color: "#0369a1",
    flex: 1,
  },
  uploadBtn: {
    padding: "0.5rem 1rem",
    background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "0.85rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  orDivider: {
    textAlign: "center",
    color: "#6b7280",
    fontSize: "0.9rem",
    fontWeight: "600",
    position: "relative",
  },
  urlOption: {
    display: "flex",
    flexDirection: "column",
  },
  imagePreview: {
    marginTop: "1rem",
  },
  imagePreviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.5rem",
  },
  imagePreviewLabel: {
    fontSize: "0.9rem",
    fontWeight: "600",
    color: "#374151",
  },
  imageSuccess: {
    fontSize: "0.85rem",
    fontWeight: "600",
    color: "#10b981",
  },
  imageDefault: {
    fontSize: "0.85rem",
    fontWeight: "600",
    color: "#6b7280",
  },
  categoryImageNote: {
    fontSize: "0.8rem",
    color: "#6b7280",
    fontStyle: "italic",
    marginTop: "0.5rem",
    textAlign: "center",
    lineHeight: "1.4",
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "200px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
};

export default CreateCampaign;